'use client';
import { useCallback } from 'react';
import { useToast } from '@practium/ui';
import { useParameters } from './useParameters';
import { exportParametersToExcel, exportParametersToPdf } from '../utils/parametersExport';
import type { StateReferenceDTO } from '../types/parameters.types';

export const useParametersPage = () => {
	const { error: toastError, success: toastSuccess } = useToast();

	const onError = useCallback((msg: string) => toastError(msg), [toastError]);

	const parametersState = useParameters(onError);

	const handleExportExcel = useCallback(() => {
		const { selectedRegion, stateReference } = parametersState;
		if (!selectedRegion || !stateReference) {
			toastError('Selecione uma região antes de exportar.');
			return;
		}
		exportParametersToExcel(selectedRegion.name, stateReference);
	}, [parametersState, toastError]);

	const handleExportPdf = useCallback(() => {
		const { selectedRegion, stateReference } = parametersState;
		if (!selectedRegion || !stateReference) {
			toastError('Selecione uma região antes de exportar.');
			return;
		}
		exportParametersToPdf(selectedRegion.name, stateReference);
	}, [parametersState, toastError]);

	const handleUpdate = useCallback(
		async (updated: StateReferenceDTO) => {
			try {
				await parametersState.updateStateReference(updated);
				toastSuccess('Parâmetros atualizados com sucesso!');
			} catch {
				toastError('Erro ao salvar parâmetros. Tente novamente.');
			}
		},
		[parametersState.updateStateReference, toastSuccess, toastError]
	);

	const handleCreateRegion = useCallback(
		(name: string, stateCodes: string[]) => {
			parametersState.createRegion(name, stateCodes);
			toastSuccess('Região criada com sucesso!');
		},
		[parametersState.createRegion, toastSuccess]
	);

	const handleUpdateRegion = useCallback(
		(regionId: string, name: string, stateCodes: string[]) => {
			parametersState.updateRegion(regionId, name, stateCodes);
			toastSuccess('Região atualizada com sucesso!');
		},
		[parametersState.updateRegion, toastSuccess]
	);

	return {
		...parametersState,
		handleExportExcel,
		handleExportPdf,
		handleUpdate,
		handleCreateRegion,
		handleUpdateRegion,
	};
};
